import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { Assignment, Programme, Review, Submission } from "@/types";

interface WeeklyScoreListProps {
  program: Programme | null;
  assignments: Assignment[];
  submissions: Submission[];
  reviews: Review[];
}

interface WeekRow {
  week: number;
  score: number | null;
  inProgress: boolean;
}

export function WeeklyScoreList({
  program,
  assignments,
  submissions,
  reviews,
}: WeeklyScoreListProps) {
  const totalWeeks = program?.total_weeks ?? 4;
  const currentWeek = program?.current_week ?? 1;

  const weeklyScores: WeekRow[] = [];
  for (let week = 1; week <= totalWeeks; week++) {
    const weekAssignments = assignments.filter((a) => a.week_number === week);

    const scores: number[] = [];
    for (const a of weekAssignments) {
      const submission = submissions.find((s) => s.assignment_id === a.id);
      const review = submission ? reviews.find((r) => r.submission_id === submission.id) : null;
      const isClosed = a.time_status === "closed";
      const isFinal = review?.status === "admin_confirmed" || review?.status === "admin_updated";

      // Only signed-off reviews count; a closed window with no submission is
      // a 0, while submitted work in review keeps the week incomplete.
      if (isFinal) {
        scores.push(review?.final_score ?? 0);
      } else if (isClosed && !submission) {
        scores.push(0);
      }
    }

    // A week is complete once every assignment in it has a score
    const isComplete = weekAssignments.length > 0 && scores.length === weekAssignments.length;
    const weekScore = isComplete
      ? Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length)
      : null;

    weeklyScores.push({
      week,
      score: weekScore,
      inProgress: weekScore === null && week === currentWeek,
    });
  }

  return (
    <Card>
      <CardHeader className="pb-0">
        <div>
          <p className="text-[10px] font-bold text-content-muted uppercase tracking-[2px]">
            Progress
          </p>
          <h3 className="text-xl font-extrabold text-card-foreground mt-0.5">Weekly Feedback</h3>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="flex flex-col gap-5">
          {weeklyScores.map((item) => (
            <div key={item.week} className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <span className="text-[11.5px] font-bold uppercase tracking-[.14em] text-[#5C6E63]">
                  Week {item.week}
                </span>
                <span className="flex items-baseline gap-2">
                  {item.inProgress && (
                    <span className="whitespace-nowrap font-serif text-xs italic text-content-muted">
                      In progress
                    </span>
                  )}
                  <span
                    className={`text-[17px] font-bold ${item.score !== null ? "text-card-foreground" : "text-content-muted"}`}
                  >
                    {item.score !== null ? item.score : "–"}
                  </span>
                </span>
              </div>
              <div className="h-[5px] overflow-hidden rounded-full bg-[#EFECDE]">
                {item.score !== null ? (
                  <div
                    className="h-full rounded-full bg-brand transition-[width] duration-500"
                    style={{ width: `${Math.min(item.score, 100)}%` }}
                  />
                ) : item.inProgress ? (
                  <div className="h-full w-[6%] rounded-full bg-content-muted/40" />
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
